"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const material_1 = require("@mui/material");
const SelectionContext_1 = require("../../context/SelectionContext");
const api_1 = require("../../api");
const SessionContext_1 = require("../../context/SessionContext");
const GenericSelectionOverlay = ({ code, isDragging = false, title = "Document Preview", iframeStyle }) => {
    const [isSelecting, setIsSelecting] = react_1.default.useState(false);
    const [selectionStart, setSelectionStart] = react_1.default.useState(null);
    const [selectionRect, setSelectionRect] = react_1.default.useState(null);
    const iframeRef = react_1.default.useRef(null);
    const overlayRefs = react_1.default.useRef([]);
    const closeButtonRef = react_1.default.useRef(null);
    const { isSelectionMode, setIsSelectionMode, setSelectedHtml, clearSelection } = (0, SelectionContext_1.useSelection)();
    const { session } = (0, SessionContext_1.useSession)();
    const docType = (session === null || session === void 0 ? void 0 : session.docType) || api_1.DOC_TYPE.FORM;
    // Define selectors per document type
    const selectorsForDocType = {
        [api_1.DOC_TYPE.FORM]: [
            '.form-group', '.form-item', '.field-group', '.input-group',
            '.form-control-group', '.field', '[data-field]', 'input', 'select', 'textarea', 'label'
        ],
        [api_1.DOC_TYPE.WORD]: ['p', 'h1', 'h2', 'h3', 'table', 'li'],
        [api_1.DOC_TYPE.PDF]: ['p', 'div', 'table', 'li', 'h1', 'h2', 'h3'],
        [api_1.DOC_TYPE.PPT]: ['.slide', '.slide-content', 'h1', 'h2', 'p']
    };
    const clearIframeOverlays = react_1.default.useCallback(() => {
        var _a;
        const doc = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentDocument;
        if (!doc)
            return;
        overlayRefs.current.forEach(node => node.remove());
        overlayRefs.current = [];
        if (closeButtonRef.current) {
            closeButtonRef.current.remove();
            closeButtonRef.current = null;
        }
    }, []);
    const drawIframeOverlays = react_1.default.useCallback((elements) => {
        const iframe = iframeRef.current;
        if (!iframe || !elements.length)
            return;
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        clearIframeOverlays();
        let union = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
        elements.forEach((el) => {
            const r = el.getBoundingClientRect();
            const top = r.top + win.scrollY;
            const left = r.left + win.scrollX;
            const overlay = doc.createElement('div');
            overlay.style.position = 'absolute';
            overlay.style.left = `${left}px`;
            overlay.style.top = `${top}px`;
            overlay.style.width = `${r.width}px`;
            overlay.style.height = `${r.height}px`;
            overlay.style.border = '2px dashed #9c27b0';
            overlay.style.background = 'rgba(156,39,176,0.08)';
            overlay.style.zIndex = '2147483646';
            overlay.style.pointerEvents = 'none';
            doc.body.appendChild(overlay);
            overlayRefs.current.push(overlay);
            union.left = Math.min(union.left, left);
            union.top = Math.min(union.top, top);
            union.right = Math.max(union.right, left + r.width);
            union.bottom = Math.max(union.bottom, top + r.height);
        });
        const close = doc.createElement('div');
        close.style.position = 'absolute';
        close.style.left = `${union.right - 10}px`;
        close.style.top = `${union.top - 10}px`;
        close.style.width = '20px';
        close.style.height = '20px';
        close.style.borderRadius = '50%';
        close.style.background = '#9c27b0';
        close.style.color = '#fff';
        close.style.display = 'flex';
        close.style.alignItems = 'center';
        close.style.justifyContent = 'center';
        close.style.cursor = 'pointer';
        close.style.zIndex = '2147483647';
        close.textContent = '×';
        close.addEventListener('click', () => {
            clearIframeOverlays();
            clearSelection();
        });
        doc.body.appendChild(close);
        closeButtonRef.current = close;
    }, [clearIframeOverlays, clearSelection]);
    react_1.default.useEffect(() => {
        return () => clearIframeOverlays(); // cleanup on unmount or code change
    }, [code, clearIframeOverlays]);
    react_1.default.useEffect(() => {
        if (isSelectionMode) {
            clearIframeOverlays();
        }
    }, [isSelectionMode, clearIframeOverlays]);
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: { position: 'relative', height: '100%' }, children: [(0, jsx_runtime_1.jsx)("iframe", { ref: iframeRef, srcDoc: code, style: Object.assign({ width: '100%', height: '100%', border: 'none', minHeight: '300px', pointerEvents: isDragging ? 'none' : 'auto' }, iframeStyle), title: title, sandbox: "allow-scripts allow-same-origin" }), isSelectionMode && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: 'absolute', inset: 0, zIndex: 9, cursor: 'crosshair' }, onMouseDown: (e) => {
                    const overlay = e.currentTarget;
                    const rect = overlay.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    setIsSelecting(true);
                    setSelectionStart({ x, y });
                    setSelectionRect({ x, y, w: 0, h: 0 });
                }, onMouseMove: (e) => {
                    if (!isSelecting || !selectionStart)
                        return;
                    const overlay = e.currentTarget;
                    const rect = overlay.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    const w = Math.abs(x - selectionStart.x);
                    const h = Math.abs(y - selectionStart.y);
                    const sx = Math.min(x, selectionStart.x);
                    const sy = Math.min(y, selectionStart.y);
                    setSelectionRect({ x: sx, y: sy, w, h });
                }, onMouseUp: (e) => {
                    setIsSelecting(false);
                    if (!selectionRect || !iframeRef.current) {
                        setSelectionRect(null);
                        setSelectionStart(null);
                        return;
                    }
                    try {
                        const overlay = e.currentTarget;
                        const overlayRect = overlay.getBoundingClientRect();
                        const iframeEl = iframeRef.current;
                        const iframeRect = iframeEl.getBoundingClientRect();
                        const doc = iframeEl.contentDocument;
                        const win = iframeEl.contentWindow;
                        if (!doc || !win)
                            throw new Error('No iframe document');
                        const selLeft = overlayRect.left + selectionRect.x;
                        const selTop = overlayRect.top + selectionRect.y;
                        const selRight = selLeft + selectionRect.w;
                        const selBottom = selTop + selectionRect.h;
                        const toPageRect = (r) => ({
                            left: iframeRect.left + r.left,
                            top: iframeRect.top + r.top,
                            right: iframeRect.left + r.right,
                            bottom: iframeRect.top + r.bottom,
                            width: r.width,
                            height: r.height,
                        });
                        const intersects = (r) => {
                            return !(r.right < selLeft || r.left > selRight || r.bottom < selTop || r.top > selBottom);
                        };
                        const candidates = Array.from(doc.body.querySelectorAll(selectorsForDocType[docType].join(',')));
                        const intersecting = candidates.filter((el) => intersects(toPageRect(el.getBoundingClientRect())));
                        if (intersecting.length > 0) {
                            const html = intersecting.map(el => el.outerHTML).join('\n');
                            setSelectedHtml(html);
                            drawIframeOverlays(intersecting);
                            setIsSelectionMode(false);
                        }
                        else {
                            setSelectedHtml('');
                            clearIframeOverlays();
                        }
                    }
                    catch (_a) {
                        setSelectedHtml('');
                        clearIframeOverlays();
                    }
                    setSelectionRect(null);
                    setSelectionStart(null);
                }, children: selectionRect && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                        position: 'absolute',
                        left: selectionRect.x,
                        top: selectionRect.y,
                        width: selectionRect.w,
                        height: selectionRect.h,
                        border: '2px dashed #1976d2',
                        backgroundColor: 'rgba(25,118,210,0.08)'
                    } })) }))] }));
};
exports.default = GenericSelectionOverlay;
